import { useState, useEffect, useRef } from 'react';
import { toast, Toaster } from 'sonner';
import { Button } from '../Components/UI/button';
import { Input } from '../Components/UI/input';
import { Label } from '../Components/UI/label';
import { Card, CardContent, CardHeader } from '../Components/UI/card';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../Components/UI/tooltip';
import MobileInput from '../Components/Common/MobileInput';
import CountdownTimer from '../Components/Common/CountDownTimer';
import { checkIfExists, sendOTP, signup, login, respondInvitation } from './LoginAPI';
import { isLive, isMobileApp, isMobileBrowser, isMobileDevice, getSalesMode, SCRM_LOGIN_MODE, getAt99, getUploadUrl } from '../Utils/Utilities';
import { countries } from '../Components/Enums/DataEnums';
import useIsMobile from '../Hooks/useIsMobile';
import { useNavigate } from 'react-router-dom';
import FlashOfferCard from "../Modules/Landing/FlashOfferCard";
import { Tag, Building2, Check, Mail } from "lucide-react";
import SahiPrinciplesModal from '../Components/Common/SahiPrinciplesModal';

// Validate email
const validateEmail = (email) => {
  const regex = /^([a-zA-Z0-9_.+-])+\@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
  return regex.test(email);
};

// Validate signup
const validateSignup = (userDetails) => {
  if (!userDetails.name || !userDetails.companyName) {
    toast.error('Please enter your name and business name.');
    return false;
  }
  if (!userDetails.email || !validateEmail(userDetails.email)) {
    toast.error('Please enter valid email address.');
    return false;
  }
  if ((userDetails.countryCode === '91' && userDetails.mobile.length !== 10) ||
    (userDetails.countryCode !== '91' && (userDetails.mobile.length > 15 || userDetails.mobile.length < 5)) ||
    userDetails.mobile.startsWith('0')) {
    toast.error('Please enter a valid mobile number.');
    return false;
  }
  if (!userDetails.countryCode) {
    toast.error('Please select country code.');
    return false;
  }
  return true;
};

// Validate mobile
const validateMobile = (mobile, cc) => {
  if (!cc) {
    toast.error('Please select country code.');
    return false;
  }

  const validCountry = countries.find(country => country.CC === cc);
  if (!validCountry) {
    toast.error('Invalid country code. Please select a valid country.');
    return false;
  }

  if (!mobile) {
    toast.error('Please enter mobile number.');
    return false;
  }
  if ((cc === '91' && mobile.length !== 10) ||
    (cc !== '91' && (mobile.length > 15 || mobile.length < 5)) ||
    mobile.startsWith('0')) {
    toast.error('Please verify your link.');
    window.location.href = '/'
    return false;
  }
  return true;
};

const LoginPopup = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const urlParams = new URLSearchParams(window.location.search);

  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isAndroid = /android/i.test(userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;

  const iParam = urlParams.get('i') || '';
  let srcParam = '';

  const getSignUpSrc = () => {
    const onboarding = urlParams.get('onboarding');
    if (onboarding && onboarding !== 'true' && onboarding !== '1') {
      return `IND_${onboarding.toUpperCase()}`;
    }

    let src = "";
    if (isMobileBrowser()) { // For mobile browser
      src = 'Mobile'
    }
    else if (isMobileApp()) {
      if (isAndroid) {
        src = 'Android'; // For andriod app
      }
      else if (isIOS) {
        src = 'IOS';  // Fr ios app
      }
    }
    else {
      src = 'Web'; // For web browser
    }
    return src;
  }

  let cookieSrc = localStorage.getItem("SrcParam") || '';
  let urlSrc = urlParams.get('src') || '';
  if (urlSrc != '') {
    srcParam = urlSrc;
  }
  else if (cookieSrc != '') {
    srcParam = cookieSrc;
  }
  else {
    srcParam = getSignUpSrc();
  }

  let urlCountryCode = '91';
  let urlMobile = '';
  let isFromExternalLink = false;

  if (iParam) {
    const parts = iParam.split('_');
    if (parts.length === 2) {
      urlCountryCode = parts[0];
      urlMobile = parts[1];
      isFromExternalLink = true;
    }
  }

  // Get other parameters
  const modeParam = urlParams.get('m') || '';  // Mode
  const connIDParam = urlParams.get('c') || '';  // ConnID
  const threadIDParam = urlParams.get('t') || '';  // ThreadID

  const [showPage, setShowPage] = useState(1);
  const [isLocked, setIsLocked] = useState(isFromExternalLink);
  const [initialCheckDone, setInitialCheckDone] = useState(!isFromExternalLink);
  const [isProcessingLead, setIsProcessingLead] = useState(false);

  // Handle Enter key press for form submission
  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  const [userDetails, setUserDetails] = useState({
    companyName: '',
    name: '',
    mobile: urlMobile,
    countryCode: urlCountryCode,
    countryID: 1,
    email: '',
    otp: '',
    password: '',
    mode: modeParam,
    connID: connIDParam,
    threadID: threadIDParam,
    src: srcParam,
    signUpSrc: getSignUpSrc(),
  });

  const [otp, setOTP] = useState(['', '', '', '']);
  const [pin, setPin] = useState(['', '', '', '']);
  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(0);
  const [isFromSignup, setIsFromSignup] = useState(false);
  const [showSignupFields, setShowSignupFields] = useState(false);
  const [userData, setUserData] = useState(null);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [pendingInviteAgency, setPendingInviteAgency] = useState(null);
  const [existingSignUpSrc, setExistingSignUpSrc] = useState('');
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsCheckbox, setShowTermsCheckbox] = useState(false);
  const [showPrinciplesModal, setShowPrinciplesModal] = useState(false);
  const otpRefs = useRef([]);
  const pinRefs = useRef([]);
  const nameInputRef = useRef(null);
  const isNotMobileBrowser = !isMobileBrowser();

  // Add refs for other pages
  const companyNameInputRef = useRef(null);

  useEffect(() => {
    if (showPage === 1 && nameInputRef.current && !isCheckingUser && !isProcessingLead) {
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
    }
    if (showPage === 2 && nameInputRef.current) {
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
    }
    if (showPage === 3 && otpRefs.current[0]) {
      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 100);
    }
    if (showPage === 4 && pinRefs.current[0]) {
      setTimeout(() => {
        pinRefs.current[0]?.focus();
      }, 100);
    }

    const handleMessage = (event) => {
      if (event.data.type === 'CLOSE_LOGIN_POPUP') {
        window.close();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [showPage, isCheckingUser, isProcessingLead]);

  // Auto-check if mobile is prefilled from external link
  useEffect(() => {
    // Check jarvis present or not
    const jarvis = localStorage.getItem("Jarvis");
    if (jarvis) {
      window.location.replace('/Web');
      return;
    }
    if (isFromExternalLink && urlMobile && !initialCheckDone) {
      setIsCheckingUser(true);
      setTimeout(() => {
        checkUserExistsWithoutValidation();
      }, 200);
      setInitialCheckDone(true);
    }
  }, []);

  const trimField = (key, value) => {
    const trimmed = value.trim();
    setUserDetails(prev => ({ ...prev, [key]: trimmed }));
  };

  // Check if all required fields are filled and valid
  const areAllFieldsValid = (details) => {
    return details.name &&
      details.name.trim() !== '' &&
      details.companyName &&
      details.companyName.trim() !== '' &&
      details.email &&
      validateEmail(details.email);
  };

  // Check if user exists on mobile submission (when signup fields are hidden)
  const handleCheckMobile = async () => {
    if (isCheckingUser) return;
    if (!validateMobile(userDetails.mobile, userDetails.countryCode)) return;
    setIsCheckingUser(true);
    try {
      const validateUser = await checkIfExists(userDetails.mobile, userDetails.countryCode);
      if (validateUser?.Table?.length > 0) {
        const userStatus = validateUser.Table[0];

        if (userStatus.S) {
          // Existing user -> Login flow
          setIsFromSignup(false);
          setShowSignupFields(false);
          setPendingInvites([]);
          setPendingInviteAgency(null);
          const firstAgency = validateUser.Table1?.find(a => a.Status === 1);
          setSelectedAgency(firstAgency?.AgencyID || 0);
          if (firstAgency?.SignUpSrc) {
            setExistingSignUpSrc(firstAgency.SignUpSrc);
          }
          if (firstAgency && firstAgency.TermsDT) {
            setShowTermsCheckbox(false);
            setAcceptedTerms(true);
          } else {
            setShowTermsCheckbox(true);
            setAcceptedTerms(false);
          }
          setShowPage(4);
        } else {
          // Check if fresh user has pending invitations
          const pending = validateUser.Table1?.filter(a => Number(a.Status) === 0);
          if (userStatus.P && pending?.length > 0) {
            setPendingInvites(pending);
            setPendingInviteAgency(null);
            setShowSignupFields(false);
            setIsFromSignup(false);
            return;
          }

          // New user/Lead -> Signup flow
          setPendingInvites([]);
          setPendingInviteAgency(null);
          setIsFromSignup(true);
          setShowSignupFields(true);

          if (userStatus.L && validateUser.Table1 && validateUser.Table1.length > 0) {
            const leadInfo = validateUser.Table1[0];
            setUserDetails(prev => ({
              ...prev,
              name: leadInfo.Name || '',
              companyName: leadInfo.CompanyName || '',
              email: leadInfo.Email || '',
            }));
          }
        }
      }
    } catch (error) {
      toast.error(error.message || 'Error checking user profile.');
    } finally {
      setIsCheckingUser(false);
    }
  };

  // Check user exists without validation (used for auto-check on page load)
  const checkUserExistsWithoutValidation = async () => {
    if (!userDetails.mobile || !userDetails.countryCode) {
      setIsCheckingUser(false);
      return;
    }
    try {
      const validateUser = await checkIfExists(userDetails.mobile, userDetails.countryCode);
      if (validateUser?.Table?.length > 0) {
        const userStatus = validateUser.Table[0];

        // User is already signed up
        if (userStatus.S) {
          if (SCRM_LOGIN_MODE === "MultiAgency") {
            setIsFromSignup(false);
            const firstAgency = validateUser.Table1?.find(a => a.Status === 1);
            setSelectedAgency(firstAgency?.AgencyID || 0);
            if (firstAgency && firstAgency.TermsDT) {
              setShowTermsCheckbox(false);
              setAcceptedTerms(true);
            } else {
              setShowTermsCheckbox(true);
              setAcceptedTerms(false);
            }
            setShowSignupFields(false);
            setShowPage(4);
            return;
          }
          if (validateUser?.Table1 && validateUser.Table1.length > 1) {
            setAgencies(validateUser.Table1);
            setShowPage(6);
          } else if (validateUser?.Table1 && validateUser.Table1.length === 1) {
            const userInfo = validateUser.Table1.find(a => a.Status === 1);
            if (userInfo) {
              if (userInfo.SignUpSrc) {
                setExistingSignUpSrc(userInfo.SignUpSrc);
              }
              setUserDetails(prev => ({
                ...prev,
                name: userInfo.Name || '',
                companyName: userInfo.CompanyName || '',
                email: userInfo.Email || '',
              }));
              setSelectedAgency(userInfo.AgencyID || 0);
              if (userInfo.TermsDT) {
                setShowTermsCheckbox(false);
                setAcceptedTerms(true);
              } else {
                setShowTermsCheckbox(true);
                setAcceptedTerms(false);
              }
            }
            setShowPage(4);
          } else {
            setShowPage(4);
          }
        }
        // Pending invitations for fresh user
        else if (userStatus.P && validateUser.Table1?.length > 0) {
          const pending = validateUser.Table1.filter(a => Number(a.Status) === 0);
          if (pending.length > 0) {
            setPendingInvites(pending);
            setPendingInviteAgency(null);
            setShowSignupFields(false);
            setIsFromSignup(false);
            setShowPage(1);
            return;
          }
        }
        // User is a lead (L property is true)
        else if (userStatus.L) {
          setIsFromSignup(true);
          setShowSignupFields(true);
          setShowPage(1);

          if (validateUser?.Table1 && validateUser.Table1.length > 0) {
            const leadInfo = validateUser.Table1[0];
            setUserDetails(prev => ({
              ...prev,
              name: leadInfo.Name || prev.name,
              companyName: leadInfo.CompanyName || prev.companyName,
              email: leadInfo.Email || prev.email,
            }));
          }
        }
        // New user with no data
        else {
          setIsFromSignup(true);
          setShowSignupFields(true);
          setShowPage(1);
        }
      }
    } catch (error) {
      toast.error(error.message || 'Unable to connect to server.');
    } finally {
      setIsCheckingUser(false);
      setIsProcessingLead(false);
    }
  };

  const handleCheckExists = async () => {
    if (showSignupFields && !acceptedTerms) {
      setShowPrinciplesModal(true);
      return;
    }
    if (isCheckingUser) return;
    if (!validateMobile(userDetails.mobile, userDetails.countryCode)) return;

    setIsCheckingUser(true);
    try {
      const validateUser = await checkIfExists(userDetails.mobile, userDetails.countryCode);
      if (validateUser?.Table?.length > 0) {
        const userStatus = validateUser.Table[0];

        // Check if user is not signed up (new user or lead)
        if (!userStatus.S) {
          setIsFromSignup(true);
          setShowSignupFields(true);
          // If user is a lead, pre-fill data
          if (userStatus.L && validateUser.Table1 && validateUser.Table1.length > 0) {
            const leadInfo = validateUser.Table1[0];
            setUserDetails(prev => ({
              ...prev,
              name: leadInfo.Name || prev.name,
              companyName: leadInfo.CompanyName || prev.companyName,
              email: leadInfo.Email || prev.email,
            }));

            // Show form with pre-filled data for user to review & proceed
            setShowPage(1);
            return;
          }

          if (showPage === 1) {
            if (!userDetails.name || !userDetails.companyName) {
              toast.error('Please enter your name and business name.');
              setIsCheckingUser(false);
              return;
            }
            if (!userDetails.email || !validateEmail(userDetails.email)) {
              toast.error('Please enter valid email address.');
              setIsCheckingUser(false);
              return;
            }
            await handleSendOTP(false);
            return;
          } else {
            setShowPage(2);
            if (validateUser.Table1 && validateUser.Table1.length > 0) {
              const leadInfo = validateUser.Table1[0];
              setUserDetails(prev => ({
                ...prev,
                name: leadInfo.Name || prev.name,
                companyName: leadInfo.CompanyName || prev.companyName,
                email: leadInfo.Email || prev.email,
              }));
            }
          }
        } else {
          // User is signed up - go to login flow
          if (SCRM_LOGIN_MODE === "MultiAgency") {
            setIsFromSignup(false);
            setShowSignupFields(false);
            const firstAgency = validateUser.Table1?.find(a => a.Status === 1);
            setSelectedAgency(firstAgency?.AgencyID || 0);
            if (firstAgency && firstAgency.TermsDT) {
              setShowTermsCheckbox(false);
              setAcceptedTerms(true);
            } else {
              setShowTermsCheckbox(true);
              setAcceptedTerms(false);
            }
            await handleSendOTP(false);
            return;
          }
          if (validateUser?.Table1 && validateUser.Table1.length > 1) {
            setAgencies(validateUser.Table1);
            setShowPage(6);
          } else if (validateUser?.Table1 && validateUser.Table1.length === 1) {
            const userInfo = validateUser.Table1.find(a => a.Status === 1);
            if (userInfo) {
              setUserDetails(prev => ({
                ...prev,
                name: userInfo.Name || '',
                companyName: userInfo.CompanyName || '',
                email: userInfo.Email || '',
              }));
              setSelectedAgency(userInfo.AgencyID || 0);
              if (userInfo.TermsDT) {
                setShowTermsCheckbox(false);
                setAcceptedTerms(true);
              } else {
                setShowTermsCheckbox(true);
                setAcceptedTerms(false);
              }
            }
            setShowPage(4);
          } else {
            setShowPage(4);
          }
        }
      }
    } catch (error) {
      toast.error(error.message || 'Unable to connect to server. Please try again later.');
    } finally {
      setIsCheckingUser(false);
      setIsProcessingLead(false);
    }
  };

  const handleAgencySelect = (agencyID) => {
    setSelectedAgency(agencyID);
    const agencyData = agencies.find(a => a.AgencyID === agencyID);
    if (agencyData) {
      setUserDetails(prev => ({
        ...prev,
        name: agencyData.Name || '',
        companyName: agencyData.CompanyName || '',
        email: agencyData.Email || '',
      }));
      if (agencyData.TermsDT) {
        setShowTermsCheckbox(false);
        setAcceptedTerms(true);
      } else {
        setShowTermsCheckbox(true);
        setAcceptedTerms(false);
      }
    }
    setShowPage(4);
  };

  const handleAcceptPendingInvite = async (agency) => {
    try {
      setIsCheckingUser(true);
      await sendOTP(userDetails.mobile, userDetails.countryCode, false, true);
      toast.success('Your PIN has been sent to your mobile number via WhatsApp.');

      setPendingInviteAgency(agency);
      setSelectedAgency(agency.AgencyID);
      setUserDetails(prev => ({
        ...prev,
        name: agency.Name || prev.name,
        companyName: agency.CompanyName || prev.companyName,
        email: agency.Email || prev.email,
      }));
      setPin(['', '', '', '']);
      setAcceptedTerms(true);
      setShowTermsCheckbox(false);
      setShowPage(4);
    } catch (error) {
      toast.error(error.message || 'Failed to send PIN. Please try again.');
    } finally {
      setIsCheckingUser(false);
    }
  };

  const handleAcceptLoginInvitation = async (agency, e) => {
    e?.stopPropagation();
    try {
      await respondInvitation(userDetails.mobile, userDetails.countryCode, agency.AgencyID, 'ACCEPT');
      toast.success(`Invitation accepted for ${agency.CompanyName}!`);
      // Update agency status in local state
      setAgencies(prev => prev.map(a => Number(a.AgencyID) === Number(agency.AgencyID) ? { ...a, Status: 1 } : a));
      handleAgencySelect(agency.AgencyID);
    } catch (error) {
      toast.error(error.message || 'Failed to accept invitation.');
    }
  };

  const handleRejectLoginInvitation = async (agency, e) => {
    e?.stopPropagation();
    try {
      await respondInvitation(userDetails.mobile, userDetails.countryCode, agency.AgencyID, 'REJECT');
      toast.success('Invitation declined.');
      setAgencies(prev => prev.filter(a => Number(a.AgencyID) !== Number(agency.AgencyID)));
    } catch (error) {
      toast.error(error.message || 'Failed to decline invitation.');
    }
  };

  const handleSendOTP = async (fromResend = false, isReset = false) => {
    // Validate signup/login details if not resending and not resetting
    if (!fromResend && !isReset) {
      if (isFromSignup) {
        if (!validateSignup(userDetails)) return;
      } else {
        if (!validateMobile(userDetails.mobile, userDetails.countryCode)) return;
      }
    }

    try {
      const result = await sendOTP(userDetails.mobile, userDetails.countryCode, fromResend, isReset);
      if (result.success) {
        if (fromResend) {
          toast.success(result.message);
        } else {
          setOTP(['', '', '', '']);
          setShowPage(3);
        }
      }
    } catch (error) {
      toast.error(error.message || 'Unable to send OTP. Please try again.');
    } finally {
      setIsCheckingUser(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (value.length > 1) {
      const cleanOTP = value.replace(/\D/g, '');
      if (!cleanOTP) {
        toast.error('Please enter numeric OTP only.');
        return;
      }
      const toFill = cleanOTP.slice(0, 4 - index);
      const newOTP = [...otp];
      for (let i = 0; i < toFill.length; i++) {
        newOTP[index + i] = toFill[i];
      }
      setOTP(newOTP);

      if (newOTP.every(digit => digit && digit.length === 1)) {
        setTimeout(() => {
          otpRefs.current[3]?.focus();
          setTimeout(() => {
            if (SCRM_LOGIN_MODE === "MultiAgency" && !isFromSignup) {
              handleOTPLogin(newOTP);
            } else {
              handleSignupWithOTP(newOTP);
            }
          }, 100);
        }, 50);
      } else {
        const nextEmptyIndex = newOTP.findIndex(digit => !digit);
        setTimeout(() => {
          if (nextEmptyIndex !== -1) {
            otpRefs.current[nextEmptyIndex]?.focus();
          } else {
            otpRefs.current[3]?.focus();
          }
        }, 50);
      }
      return;
    }

    const newOTP = [...otp];
    newOTP[index] = value;
    setOTP(newOTP);

    // Move to next input if value entered and not last field
    if (value && index < 3) {
      otpRefs.current[index + 1].focus();
    }

    // Auto-submit when last digit is entered and all fields are filled
    if (value && index === 3) {
      // Check if all OTP fields are now filled
      if (newOTP.every(digit => digit && digit.length === 1)) {
        setTimeout(() => {
          if (SCRM_LOGIN_MODE === "MultiAgency" && !isFromSignup) {
            handleOTPLogin(newOTP);
          } else {
            handleSignupWithOTP(newOTP);
          }
        }, 200);
      }
    }
  };

  const handleOTPLogin = async (otpArray) => {
    const fullOTP = otpArray.join('');
    if (fullOTP.length !== 4) {
      toast.error('Please enter a valid OTP.');
      return;
    }
    try {
      const jarvisData = await login(userDetails.mobile, userDetails.countryCode, fullOTP, selectedAgency, acceptedTerms);
      handleLoginSuccess(jarvisData);
    } catch (error) {
      toast.error(error.message || 'Login failed. Please try again.');
    }
  };

  const handleSignupWithOTP = async (otpArray) => {
    const fullOTP = otpArray.join('');
    if (fullOTP.length !== 4) {
      toast.error('Please enter a valid OTP.');
      return;
    }
    if (!validateSignup(userDetails)) return;

    try {
      // Find matching country by CC (country calling code)
      const matchedCountry = countries.find(c => c.CC === userDetails.countryCode);

      // Create a new object with the correct countryID
      const updatedUserDetails = {
        ...userDetails,
        countryID: matchedCountry ? matchedCountry.CountryID : 1,
        otp: fullOTP,
      };
      const jarvisData = await signup({ ...updatedUserDetails, otp: fullOTP });
      setUserData(jarvisData);
      setIsFromSignup(true);
      // setShowPage(5);
      handleLoginSuccess(jarvisData, true);
    } catch (error) {
      toast.error(error.message || 'Signup failed. Please try again.');
    }
  };

  const handleSignup = async () => {
    if (otp.some(o => !o)) {
      toast.error('Please enter OTP.');
      return;
    }
    if (SCRM_LOGIN_MODE === "MultiAgency" && !isFromSignup) {
      handleOTPLogin(otp);
      return;
    }
    if (!validateSignup(userDetails)) return;
    const fullOTP = otp.join('');
    if (fullOTP.length !== 4) {
      toast.error('Please enter a valid OTP.');
      return;
    }
    try {
      // Find matching country by CC (country calling code)
      const matchedCountry = countries.find(c => c.CC === userDetails.countryCode);

      // Create a new object with the correct countryID
      const updatedUserDetails = {
        ...userDetails,
        countryID: matchedCountry ? matchedCountry.CountryID : 1,
        otp: fullOTP,
      };

      const jarvisData = await signup({ ...updatedUserDetails, otp: fullOTP });
      setUserData(jarvisData);
      setIsFromSignup(true);
      // setShowPage(5);
      handleLoginSuccess(jarvisData, true);
    } catch (error) {
      toast.error(error.message || 'Signup failed. Please try again.');
    }
  };
  const handleLogin = async (passwordOverride = null) => {
    const passwordToUse = typeof passwordOverride === 'string' ? passwordOverride : userDetails.password;
    if (!validateMobile(userDetails.mobile, userDetails.countryCode)) return;
    if (!acceptedTerms) {
      toast.error('Please confirm that you accept the terms.');
      return;
    }
    if (!passwordToUse) {
      toast.error('Please enter your PIN.');
      return;
    }
    try {
      if (pendingInviteAgency) {
        await respondInvitation(userDetails.mobile, userDetails.countryCode, pendingInviteAgency.AgencyID, 'ACCEPT', passwordToUse);
        toast.success(`Invitation accepted for ${pendingInviteAgency.CompanyName}!`);
      }
      const jarvisData = await login(userDetails.mobile, userDetails.countryCode, passwordToUse, selectedAgency || pendingInviteAgency?.AgencyID, acceptedTerms);
      handleLoginSuccess(jarvisData);
    } catch (error) {
      toast.error(error.message || 'Login failed. Please try again.');
    }
  };

  const handlePINChange = (index, value) => {
    if (value.length > 1) {
      const cleanPIN = value.replace(/\D/g, '');
      if (!cleanPIN) return;
      const toFill = cleanPIN.slice(0, 4 - index);
      const newPin = [...pin];
      for (let i = 0; i < toFill.length; i++) {
        newPin[index + i] = toFill[i];
      }
      setPin(newPin);
      const newPassword = newPin.join('');
      setUserDetails(prev => ({ ...prev, password: newPassword }));

      if (newPin.every(digit => digit && digit.length === 1)) {
        setTimeout(() => {
          pinRefs.current[3]?.focus();
          setTimeout(() => {
            handleLogin(newPassword);
          }, 100);
        }, 50);
      }
      return;
    }

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    const newPassword = newPin.join('');
    setUserDetails(prev => ({ ...prev, password: newPassword }));

    if (value && index < 3) {
      pinRefs.current[index + 1].focus();
    }

    if (value && index === 3 && newPin.every(d => d)) {
      setTimeout(() => {
        handleLogin(newPassword);
      }, 200);
    }
  };

  const handleLoginSuccess = (jarvisData, isFirstEntry = false) => {
    // Check if opened in popup/new window (has window.opener)
    if (window.opener) {
      window.opener.postMessage({
        type: 'LOGIN_SUCCESS',
        jarvis: jarvisData,
        isFirstEntry: isFirstEntry,
      }, '*');

      // Give a small delay to ensure message is received before closing
      setTimeout(() => {
        window.close();
      }, 500);
    } else {
      // Opened directly in same page/tab - handle login process
      loginProcess(jarvisData, isFirstEntry);
    }
  };

  const loginProcess = (jarvisData, isFirstEntry = false) => {
    if (!jarvisData) {
      return;
    }

    // Ensure jarvisData is in array format and has IsWeb flag
    const jarvisArray = Array.isArray(jarvisData) ? jarvisData : [jarvisData];
    jarvisArray[0].IsWeb = true;

    // If signing up from a mobile browser (isDevice = Mobile, isWeb = true, isApp = false), block web access & redirect to Download Wall
    // if (isFirstEntry && isMobileDevice() && !isMobileApp()) {
    //   localStorage.removeItem('Jarvis');
    //   localStorage.removeItem('getIDB');
    //   localStorage.setItem('SignUp', '1');
    //   window.location.replace('/Web/download?new_user=1');
    //   return;
    // }

    // Store in localStorage for authorized web sessions
    localStorage.setItem('Jarvis', JSON.stringify(jarvisArray));
    localStorage.setItem('getIDB', '1');

    // Parse URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const account = urlParams.get('acc');
    const source = urlParams.get('edn');
    const onboarding = urlParams.get('onboarding');

    const signUpSrc = existingSignUpSrc || '';
    let segmentFromSource = '';
    if (signUpSrc && signUpSrc.startsWith('IND_')) {
      const segmentName = signUpSrc.split('_')[1];
      if (segmentName) segmentFromSource = segmentName.toLowerCase();
    }

    const needsOnboarding = isFirstEntry || jarvisArray[0]?.NeedsOnboarding == 1;
    const targetSegment = onboarding || segmentFromSource;

    if (needsOnboarding) {
      localStorage.setItem('SignUp', '1');
      localStorage.setItem('needs_onboarding', '1');
      if (targetSegment) {
        localStorage.setItem('onboarding_segment', targetSegment);
      }
    } else {
      localStorage.removeItem('needs_onboarding');
      localStorage.removeItem('onboarding_segment');
    }

    // Build redirect URL
    let redirectUrl = '/Web';
    const redirectParams = [];

    if (account) {
      redirectParams.push(`isFromInd=1`);
      redirectParams.push(`rdto=${account}`);
    }

    if (source) {
      redirectParams.push(`isUp=1`);
      redirectParams.push(`edn=${source}`);
    }

    if (isFirstEntry && onboarding) {
      redirectParams.push(`onboarding=${encodeURIComponent(onboarding)}`);
    }

    if (redirectParams.length > 0) {
      redirectUrl += '?' + redirectParams.join('&');
    }

    // Redirect to main app
    window.location.replace(redirectUrl);
  };

  const signUpCookie = {
    nextAction: ""
  }

  const handleSkipFirstEntry = () => {
    signUpCookie.nextAction = 'Dashboard';
    localStorage.setItem('SignUp', JSON.stringify([signUpCookie]));
    handleLoginSuccess(userData, false);
  };

  const handleStartFirstEntry = () => {
    signUpCookie.nextAction = 'InqEntry';
    localStorage.setItem('SignUp', JSON.stringify([signUpCookie]));
    handleLoginSuccess(userData, true);
  };

  const handleResetPin = async () => {
    if (!validateMobile(userDetails.mobile, userDetails.countryCode)) return;
    try {
      let resp = await sendOTP(userDetails.mobile, userDetails.countryCode, false, true);
      if (resp.success) {
        toast.success(resp?.data?.message ? resp.data.message : resp.message);
      }
      // setShowPage(3);
    } catch (error) {
      toast.error(error.message || 'Unable to reset PIN.');
    }
  };

  const handleBack = () => {
    if (showPage === 4 || showPage === 6) {
      setShowPage(1);
      if (pendingInviteAgency) {
        setPin(['', '', '', '']);
      } else {
        setUserDetails({ companyName: '', name: '', mobile: '', countryCode: '91', countryID: 1, email: '', otp: '', password: '' });
      }
    } else {
      setShowPage(Math.max(1, showPage - 1));
    }
  };

  // Loading screen component
  const LoadingScreen = () => (
    <div className="flex flex-col items-center justify-center h-full py-20">
      <div className={`flex justify-center ${isMobile ? 'mb-8' : 'mb-12'}`}>
        <img
          src={isLive() ? "/Web/assets/Images/SahiLogo.png" : "/assets/Images/SahiLogo.png"}
          alt="SahiClub"
          className={isMobile ? 'w-40' : 'w-48'}
        />
      </div>
      <div className="flex flex-col items-center gap-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sahi-green"></div>
        {isProcessingLead && (
          <p className="text-sm text-muted-foreground">Sending verification code...</p>
        )}
      </div>
    </div>
  );

  const redirectMobileAppStore = () => {
    // const androidPlayStore = "market://details?id=com.sahicrm.androidapp";
    // const iosAppStore = "itms-apps://itunes.apple.com/in/app/sahicrm/id6745503602";
    const androidPlayStoreFallback = "https://play.google.com/store/apps/details?id=com.sahicrm.androidapp";
    const iosAppStoreFallback = "https://apps.apple.com/in/app/sahicrm/id6745503602";

    if (isAndroid) {
      // window.location.href = androidPlayStore;
      // setTimeout(() => {
      //     window.location.href = androidPlayStoreFallback;
      // }, 2000);
      window.location.href = androidPlayStoreFallback;
    } else if (isIOS) {
      // window.location.href = iosAppStore;
      // setTimeout(() => {
      //     window.location.href = iosAppStoreFallback;
      // }, 2000);
      window.location.href = iosAppStoreFallback;
    } else {
      toast.info("Please open this link on your mobile device to launch the app.");
    }
  };

  // if (srcParam && (srcParam.toLowerCase().startsWith('rt') || srcParam.toLowerCase().startsWith('lg'))) {
  //   window.location.href = '/Web/download';
  //   return null;
  // }

  return (
    <div className={`flex items-center justify-center !bg-bg-grey ${isMobile ? 'p-0 min-h-[100dvh]' : 'p-4 min-h-screen'}`}>
      {/* <Toaster position="top-center" /> */}
      <div className={`w-full ${isMobile ? '' : 'max-w-md'}`}>
        <Card className={`bg-sahi-disable border-0 !gap-0 ${isMobile ? '!shadow-none !rounded-none min-h-[100dvh] flex flex-col' : '!shadow-2xl !rounded-2xl'}  ${showPage === 6 ? 'bg-sahi-disable' : ''}`}>
          {(isCheckingUser || isProcessingLead) ? <LoadingScreen /> :
            <>
              {/* Page 1: Mobile Input */}
              {showPage === 1 && (
                <>
                  <CardHeader className={`text-center pb-4 ${isMobile ? 'pt-8' : 'pt-12'}`}>
                    <div className={`flex justify-center ${isMobile ? 'mb-6' : 'mb-8'}`}>
                      <img src={
                        isLive() ? "/Web/assets/Images/SahiLogo.png" : "/assets/Images/SahiLogo.png"
                      } alt="SahiClub" className='w-48' />
                    </div>
                    {pendingInvites.length > 0 ? (
                      <h1 className="text-xl font-semibold text-gray-900">
                        Pending Invitations
                      </h1>
                    ) : (
                      <p
                        className={`text-muted-foreground px-3 ${isMobile ? "text-sm" : "text-base"}`}
                      >
                        {showSignupFields
                          ? "Please share your basic information to get started."
                          : "Enter your mobile number to continue."}
                      </p>
                    )}
                  </CardHeader>
                  <CardContent className={isMobile ? 'px-6 pb-8' : 'px-8 pb-10'}>
                    {pendingInvites.length > 0 ? (
                      <div>
                        <div className="text-left max-h-[380px] overflow-y-auto pr-1 flex flex-col gap-2.5">
                          {pendingInvites.map((agency) => {
                            const logoPath = agency.AgencyLogo || agency.Logo;
                            const logoUrl = logoPath ? getUploadUrl(`Uploads/${logoPath}`) : null;

                            return (
                              <div
                                key={agency.AgencyID}
                                className="flex items-center justify-between gap-3 p-3.5 rounded-xl border border-gray-200 bg-white shadow-xs hover:border-gray-300 transition-all text-left"
                              >
                                {/* Left Logo */}
                                <div className="w-12 h-12 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0 overflow-hidden text-zinc-600 border border-zinc-100">
                                  {logoUrl ? (
                                    <img
                                      src={logoUrl}
                                      alt={agency.CompanyName || agency.AgencyName}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                                      }}
                                    />
                                  ) : null}
                                  <Building2
                                    className="w-6 h-6 text-zinc-500"
                                    style={{ display: logoUrl ? 'none' : 'block' }}
                                  />
                                </div>

                                {/* Middle Info */}
                                <div className="min-w-0 flex-1">
                                  <TooltipProvider delayDuration={700}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <h3 className="text-[15px] font-semibold text-gray-900 leading-tight truncate cursor-default">
                                          {agency.CompanyName || agency.AgencyName}
                                        </h3>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" align="start">
                                        <p>{agency.CompanyName || agency.AgencyName}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  {agency.Designation ? (
                                    <p className="text-xs text-gray-500 mt-0.5 truncate">{agency.Designation}</p>
                                  ) : null}
                                </div>

                                {/* Right Accept Button */}
                                <div className="shrink-0">
                                  <Button
                                    id="ga-accept-invite-btn"
                                    onClick={() => handleAcceptPendingInvite(agency)}
                                    disabled={isCheckingUser}
                                    className="h-9 px-4 text-sm font-medium whitespace-nowrap rounded-lg border border-sahi-green bg-sahi-green text-white hover:bg-transparent hover:text-sahi-green transition-all shadow-xs"
                                  >
                                    Accept
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Divider - with equal top and bottom margin */}
                        <div className="my-5 flex items-center gap-3">
                          <div className="h-px bg-gray-200 flex-1" />
                          <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">or</span>
                          <div className="h-px bg-gray-200 flex-1" />
                        </div>

                        {/* Footer / Secondary Option */}
                        <Button
                          type="button"
                          onClick={() => {
                            setPendingInvites([]);
                            setShowSignupFields(true);
                            setIsFromSignup(true);
                          }}
                          className="w-full h-11 px-4 text-sm font-medium rounded-lg border border-sahi-green bg-transparent text-sahi-green hover:bg-sahi-green hover:text-white transition-all shadow-xs"
                        >
                          Add a new Business
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className={isMobile ? (showSignupFields ? 'space-y-3 mb-4' : 'space-y-3') : (showSignupFields ? 'space-y-4 mb-5' : 'space-y-4 mb-6')}>
                          {showSignupFields && (
                            <>
                              <div className="space-y-2">
                                <Label htmlFor="name" className="text-sm font-medium text-gray-700">Name</Label>
                                <Input
                                  ref={nameInputRef}
                                  id="name"
                                  value={userDetails.name}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const capitalized = value.replace(/\b\w/g, (char) => char.toUpperCase());
                                    setUserDetails(prev => ({ ...prev, name: capitalized }));
                                  }}
                                  onBlur={(e) => trimField('name', e.target.value)}
                                  onKeyPress={(e) => handleKeyPress(e, handleCheckExists)}
                                  placeholder="e.g. Kevin Roy"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="companyName" className="text-sm font-medium text-gray-700">Business Name</Label>
                                <Input
                                  id="companyName"
                                  value={userDetails.companyName}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const capitalized = value.replace(/\b\w/g, (char) => char.toUpperCase());
                                    setUserDetails(prev => ({ ...prev, companyName: capitalized }));
                                  }}
                                  onBlur={(e) => trimField('companyName', e.target.value)}
                                  onKeyPress={(e) => handleKeyPress(e, handleCheckExists)}
                                  placeholder="e.g. Flowmark Solutions Pvt. Ltd."
                                />
                              </div>
                            </>
                          )}

                          <div className="space-y-2">
                            <Label htmlFor="mobile" className="text-sm font-medium text-gray-700">Mobile Number</Label>
                            <MobileInput
                              value={userDetails.mobile}
                              onChange={(e) => setUserDetails(prev => ({ ...prev, mobile: e.target.value }))}
                              onCountryCodeChange={(cc) => setUserDetails(prev => ({ ...prev, countryCode: cc }))}
                              onKeyPress={(e) => handleKeyPress(e, showSignupFields ? handleCheckExists : handleCheckMobile)}
                              placeholder="e.g. 9876543210"
                              className="h-12 border-sahi-green focus:border-sahi-green focus:ring-sahi-green"
                              selectedCountryCode={userDetails.countryCode}
                              disabled={isLocked}
                            />
                          </div>

                          {showSignupFields && (
                            <div className="space-y-2">
                              <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                              <Input
                                id="email"
                                type="email"
                                value={userDetails.email}
                                onChange={(e) => setUserDetails(prev => ({ ...prev, email: e.target.value }))}
                                onBlur={(e) => trimField('email', e.target.value)}
                                onKeyPress={(e) => handleKeyPress(e, handleCheckExists)}
                                placeholder="e.g. kevin@flowmark.com"
                              />
                            </div>
                          )}
                        </div>
                        {/* Terms & Privacy checkbox for Registration/Signup form */}
                        {showSignupFields && (
                          <div className="flex items-center justify-start gap-2 mt-0 mb-4 w-full px-1">
                            <input
                              type="checkbox"
                              id="acceptTermsSignup"
                              checked={acceptedTerms}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setShowPrinciplesModal(true);
                                } else {
                                  setAcceptedTerms(false);
                                }
                              }}
                              className="w-4 h-4 cursor-pointer rounded border-gray-300 text-sahi-green focus:ring-sahi-green flex-shrink-0"
                              style={{ accentColor: 'var(--sahi-green)' }}
                            />
                            <label
                              htmlFor="acceptTermsSignup"
                              className="text-gray-600 cursor-pointer select-none text-left"
                              style={{ fontSize: '13px' }}
                            >
                              I accept the{' '}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowPrinciplesModal(true);
                                }}
                                className="text-sahi-green hover:underline font-semibold inline p-0 bg-transparent border-0 cursor-pointer"
                                style={{ fontSize: '13px' }}
                              >
                                sales terms
                              </button>{' '}
                              &{' '}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setShowPrinciplesModal(true);
                                }}
                                className="text-sahi-green hover:underline font-semibold inline p-0 bg-transparent border-0 cursor-pointer"
                                style={{ fontSize: '13px' }}
                              >
                                privacy policy
                              </button>
                              .
                            </label>
                          </div>
                        )}
                        <div className={showSignupFields ? 'mt-0' : (isMobile ? 'mt-4' : 'mt-6')}>
                          <Button id="ga-verifyprofile-btn"
                            onClick={showSignupFields ? handleCheckExists : handleCheckMobile}
                            disabled={isCheckingUser}
                            className="w-full h-12 bg-sahi-green hover:border-sahi-green hover:text-sahi-green"
                          >
                            {showSignupFields ? "Get Started" : "Continue"}
                          </Button>
                        </div>
                      </>
                    )}

                    {getAt99 && <div class="mt-5 bg-sahi-green-light rounded-lg p-4 py-5 text-center animate-pulse">
                      <div class="flex items-center justify-center gap-2 mb-2">
                        <span class="text-lg font-semibold text-sahi-green ">
                          Flash Offer
                        </span>
                      </div>
                      <p class="text-lg text-sahi-dark-blue mb-1 font-semibold">
                        Start First Year @ just
                      </p>
                      <div class="flex items-baseline justify-center gap-1 mt-2">
                        <span class=" text-gray-700 font-medium mr-1">
                          <span class="text-md text-gray-700 opacity-80 font-medium mr-1">
                            ₹
                          </span>
                          <span class="line-through text-lg font-semibold">1,200</span>
                        </span>
                        <span class=" font-bold text-sahi-dark-blue">
                          <span class="text-3xl text-gray-700 opacity-80 font-medium mr-1">
                            ₹
                          </span>
                          <span class="text-5xl">99</span>
                        </span>
                        <span class="text-md text-gray-700 font-medium">
                          +GST
                        </span>
                      </div>
                    </div>}

                  </CardContent>
                </>
              )}

              {showPage === 2 && (
                <>
                  <CardHeader className={`text-center pb-6 ${isMobile ? 'pt-8' : 'pt-12'}`}>
                    <div className={`flex justify-center ${isMobile ? 'mb-6' : 'mb-8'}`}>
                      <img src={
                        isLive() ? "/Web/assets/Images/SahiLogo.png" : "/assets/Images/SahiLogo.png"
                      } alt="SahiClub" className={isMobile ? 'w-40' : 'w-48'} />
                    </div>
                    <p
                      className={`text-muted-foreground px-3 ${isMobile ? "text-sm" : "text-base"}`}
                    >
                      Please share your basic information to get started.
                    </p>
                    {/* <p className="text-base text-muted-foreground px-3">Please share your basic information to start your free {getSalesMode() === 0 ? 'CRM account' : 'CRM trial account'}.</p> */}
                  </CardHeader>
                  <CardContent className="px-8 pb-10">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="displayMobile" className="text-sm font-medium text-gray-700">Mobile Number</Label>
                        <div className="flex w-full h-11 bg-gray-100 sahi-input sahi-radius overflow-hidden border border-gray-300">
                          <div className="h-11 w-12 flex items-center justify-center px-3 border-r border-gray-300 shrink-0 bg-gray-100">
                            <span className="text-sm text-gray-600">+{userDetails.countryCode}</span>
                          </div>
                          <input
                            id="displayMobile"
                            type="text"
                            value={userDetails.mobile}
                            readOnly
                            disabled
                            className="flex-1 border-0 focus:ring-0 focus:outline-none px-3 py-2 h-full text-sm min-w-0 bg-gray-100 cursor-not-allowed text-gray-600"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-medium text-gray-700">Name</Label>
                        <Input
                          ref={nameInputRef}
                          id="name"
                          value={userDetails.name}
                          onChange={(e) => {
                            const value = e.target.value;
                            const capitalized = value.replace(/\b\w/g, (char) => char.toUpperCase());
                            setUserDetails(prev => ({ ...prev, name: capitalized }));
                          }}
                          onBlur={(e) => trimField('name', e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, () => handleSendOTP(false))}
                          placeholder="e.g. Kevin Roy"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="companyName" className="text-sm font-medium text-gray-700">Business Name</Label>
                        <Input
                          id="companyName"
                          value={userDetails.companyName}
                          onChange={(e) => {
                            const value = e.target.value;
                            const capitalized = value.replace(/\b\w/g, (char) => char.toUpperCase());
                            setUserDetails(prev => ({ ...prev, companyName: capitalized }));
                          }}
                          onBlur={(e) => trimField('companyName', e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, () => handleSendOTP(false))}
                          placeholder="e.g. Flowmark Solutions Pvt. Ltd."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={userDetails.email}
                          onChange={(e) => setUserDetails(prev => ({ ...prev, email: e.target.value }))}
                          onBlur={(e) => trimField('email', e.target.value)}
                          onKeyPress={(e) => handleKeyPress(e, () => handleSendOTP(false))}
                          placeholder="e.g. kevin@flowmark.com"
                        />
                      </div>
                    </div>
                    <Button onClick={() => handleSendOTP(false)} className="w-full h-12 mt-6 bg-sahi-green hover:border-sahi-green hover:text-sahi-green">
                      Send Verification Code
                    </Button>
                    {getAt99 && <div class="mt-5 bg-sahi-green-light rounded-lg p-4 py-5 text-center animate-pulse">
                      <div class="flex items-center justify-center gap-2 mb-2">
                        <span class="text-lg font-semibold text-sahi-green ">
                          Flash Offer
                        </span>
                      </div>
                      <p class="text-lg text-sahi-dark-blue mb-1 font-semibold">
                        Start First Year @ just
                      </p>
                      <div class="flex items-baseline justify-center gap-1 mt-2">
                        <span class=" text-gray-700 font-medium mr-1">
                          <span class="text-md text-gray-700 opacity-80 font-medium mr-1">
                            ₹
                          </span>
                          <span class="line-through text-lg font-semibold">1,200</span>
                        </span>
                        <span class=" font-bold text-sahi-dark-blue">
                          <span class="text-3xl text-gray-700 opacity-80 font-medium mr-1">
                            ₹
                          </span>
                          <span class="text-5xl">99</span>
                        </span>
                        <span class="text-md text-gray-700 font-medium">
                          +GST
                        </span>
                      </div>
                    </div>}
                  </CardContent>
                </>
              )}

              {/* Page 3: OTP */}
              {showPage === 3 && (
                <>
                  <CardHeader className="text-center pt-12 pb-6">
                    <div className="flex justify-center mb-6">
                      <img src={
                        isLive() ? "/Web/assets/Images/SahiLogo.png" : "/assets/Images/SahiLogo.png"
                      } alt="SahiClub" className="w-48" />
                    </div>
                    <h1 className="text-xl font-semibold text-gray-900 mb-3">{isFromSignup ? 'Verification' : 'Welcome Back!'}</h1>
                    <p className="text-sm text-muted-foreground px-4">
                      {isFromSignup ? (
                        <>
                          Please enter the OTP sent by WhatsApp
                          <br />
                          to +{userDetails.countryCode} {userDetails.mobile}.
                        </>
                      ) : (
                        "Please enter your PIN to login."
                      )}
                    </p>
                  </CardHeader>
                  <CardContent className="px-8 pb-10">
                    <div className="flex justify-center gap-2 mb-4">
                      {otp.map((digit, index) => (
                        <Input
                          key={index}
                          ref={el => (otpRefs.current[index] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={4}
                          value={digit}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) {
                              handleOTPChange(index, value);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Backspace' && !digit && index > 0) otpRefs.current[index - 1].focus();
                            if (e.key === 'Enter' && otp.every(o => o)) handleSignup();
                          }}
                          className="w-12 h-12 text-center text-lg font-medium"
                        />
                      ))}
                    </div>
                    <div className="text-center mb-6 mt-2">
                      <button onClick={isFromSignup ? () => handleSendOTP(true) : handleResetPin} className="text-sahi-green hover:text-sahi-green/80 text-sm font-medium">
                        {isFromSignup ? "Resend OTP" : "Forgot? Remind PIN"}
                      </button>
                    </div>
                    {isFromSignup && (
                      <div className="px-4 mb-4">
                        <p className="text-xs text-muted-foreground text-center">
                          By continuing, you agree to the <span className="text-sahi-green cursor-pointer hover:underline whitespace-nowrap">
                            <a href="https://sahicrm.com/Web/STerms.pdf" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                          </span> and{' '}
                          <span className="text-sahi-green cursor-pointer hover:underline whitespace-nowrap">
                            <a href="https://sahicrm.com/Web/policy.pdf" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                          </span>.
                        </p>
                      </div>
                    )}
                    <Button onClick={handleSignup} className="w-full h-12 bg-sahi-green hover:border-sahi-green hover:text-sahi-green" id="ga-activate-btn">
                      {isFromSignup ? "Accept & Activate" : "Login"}
                    </Button>
                  </CardContent>
                </>
              )}

              {/* Page 5: Congratulations */}
              {showPage === 5 && (
                <>
                  <CardHeader className="text-center pt-12 pb-6">
                    <div className="flex justify-center mb-8">
                      <div className="relative">
                        <div className="w-20 h-20 bg-sahi-green rounded-full flex items-center justify-center animate-pulse">
                          <div className="w-20 h-20 bg-sahi-green rounded-full flex items-center justify-center shadow-lg">
                            <svg
                              className="w-10 h-10 text-white animate-bounce"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              style={{
                                animation: 'checkmark 0.6s ease-in-out'
                              }}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={3}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                        </div>
                        {/* Success particles animation */}
                        <div className="absolute -top-2 -left-2 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></div>
                        <div className="absolute -top-1 -right-3 w-1.5 h-1.5 bg-yellow-300 rounded-full animate-ping" style={{ animationDelay: '0.2s' }}></div>
                        <div className="absolute -bottom-2 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-ping" style={{ animationDelay: '0.4s' }}></div>
                        <div className="absolute -bottom-1 -left-3 w-1.5 h-1.5 bg-yellow-300 rounded-full animate-ping" style={{ animationDelay: '0.6s' }}></div>
                      </div>
                    </div>
                    <h1 className="text-3xl font-bold text-sahi-green mb-4 animate-fade-in">Congratulations!</h1>
                    <p className="text-base text-muted-foreground px-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                      Your SahiClub {getSalesMode() === 0 ? 'account' : 'trial'} has been activated.<br /><br />
                      Let's Get Started!
                    </p>
                  </CardHeader>
                  <CardContent className="px-8 pb-10">
                    <><Button onClick={handleStartFirstEntry} className="w-full h-12 bg-sahi-green hover:border-sahi-green hover:text-sahi-green mb-3 animate-fade-in" style={{ animationDelay: '0.6s' }}>
                      {/* {getSalesMode() === 0 ? 'Go to Dashboard' : 'Start First Entry'} */}
                      Go to Dashboard
                    </Button>
                      {/* <button onClick={handleSkipFirstEntry} className="w-full text-sahi-green hover:text-sahi-green/80 text-sm font-medium animate-fade-in" style={{animationDelay: '0.8s'}}>
                      Skip to Dashboard
                    </button></> :  */}</>
                    {/* {!isNotMobileBrowser &&
                    <Button onClick={redirectMobileAppStore} className="w-full h-12 bg-sahi-green hover:border-sahi-green hover:text-sahi-green mb-3 animate-fade-in" style={{animationDelay: '0.6s'}}>
                      Download the App
                    </Button>} */}
                  </CardContent>
                </>
              )}

              {/* Page 4: Password / PIN */}
              {showPage === 4 && (
                <>
                  <CardHeader className="text-center pt-12 pb-6">
                    <div className="flex justify-center mb-6">
                      <img src={
                        isLive() ? "/Web/assets/Images/SahiLogo.png" : "/assets/Images/SahiLogo.png"
                      } alt="SahiClub" className="w-48" />
                    </div>
                    <h1 className="text-xl font-semibold text-gray-900 mb-3">
                      {pendingInviteAgency ? (pendingInviteAgency.CompanyName || pendingInviteAgency.AgencyName || 'Join Agency') : (isFromSignup ? 'Verification' : 'Welcome Back!')}
                    </h1>
                    <p className="text-sm text-muted-foreground px-4">
                      {pendingInviteAgency ? (
                        <>
                          Please enter the OTP sent by WhatsApp
                          <br />
                          to +{userDetails.countryCode} {userDetails.mobile}.
                        </>
                      ) : (
                        "Please enter your PIN to login."
                      )}
                    </p>
                  </CardHeader>
                  <CardContent className="px-8 pb-10">
                    <div className="flex justify-center gap-2 mb-4">
                      {pin.map((digit, index) => (
                        <Input
                          key={index}
                          ref={el => (pinRefs.current[index] = el)}
                          type="password"
                          inputMode="numeric"
                          maxLength={4}
                          value={digit}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) {
                              handlePINChange(index, value);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Backspace' && !digit && index > 0) pinRefs.current[index - 1].focus();
                            if (e.key === 'Enter' && pin.every(p => p)) handleLogin(pin.join(''));
                          }}
                          className="w-12 h-12 text-center text-lg font-medium"
                        />
                      ))}
                    </div>

                    <div className="text-center mb-6 mt-2">
                      <button onClick={handleResetPin} className="text-sahi-green hover:text-sahi-green/80 text-sm font-medium">
                        {pendingInviteAgency ? "Resend OTP" : "Forgot? Remind PIN"}
                      </button>
                    </div>

                    {/* Terms and Privacy Policy */}
                    {(pendingInviteAgency || isFromSignup || showTermsCheckbox) && (
                      <div className="px-4 mb-4">
                        <p className="text-xs text-muted-foreground text-center">
                          By continuing, you agree to the <span className="text-sahi-green cursor-pointer hover:underline whitespace-nowrap">
                            <a href="https://sahicrm.com/Web/STerms.pdf" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                          </span> and{' '}
                          <span className="text-sahi-green cursor-pointer hover:underline whitespace-nowrap">
                            <a href="https://sahicrm.com/Web/policy.pdf" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                          </span>.
                        </p>
                      </div>
                    )}

                    <Button onClick={() => handleLogin()} className="w-full h-12 bg-sahi-green hover:border-sahi-green hover:text-sahi-green">
                      {pendingInviteAgency ? "Accept & Activate" : "Login"}
                    </Button>
                  </CardContent>
                </>
              )}

              {/* Page 6: Agency Select */}
              {showPage === 6 && (
                <>
                  <CardHeader className="text-center pt-12 pb-6 bg-sahi-disable">
                    <div className="flex justify-center mb-6">
                      <img src={
                        isLive() ? "/Web/assets/Images/SahiLogo.png" : "/assets/Images/SahiLogo.png"
                      } alt="SahiClub" className="w-48" />
                    </div>
                    <h1 className="text-xl font-normal text-gray-800">Select Profile</h1>
                  </CardHeader>
                  <CardContent className="px-8 pb-10 bg-sahi-disable rounded-2xl">
                    <div className="space-y-3">
                      {agencies.map(agency => {
                        const isPending = Number(agency.Status) === 0;
                        if (isPending) {
                          return (
                            <div
                              key={agency.AgencyID}
                              className="p-3.5 rounded-lg border border-gray-200 bg-white shadow-xs mb-2"
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <h3 className="font-semibold text-gray-900 truncate">{agency.CompanyName}</h3>
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      Invitation
                                    </span>
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{agency.Designation}</p>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <Button
                                    size="sm"
                                    className="h-7 px-2.5 bg-sahi-green hover:bg-sahi-green/90 text-white text-xs rounded"
                                    onClick={(e) => handleAcceptLoginInvitation(agency, e)}
                                  >
                                    Accept
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2.5 border-gray-200 text-gray-600 hover:bg-gray-50 text-xs rounded"
                                    onClick={(e) => handleRejectLoginInvitation(agency, e)}
                                  >
                                    Decline
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div
                            key={agency.AgencyID}
                            onClick={() => handleAgencySelect(agency.AgencyID)}
                            className="p-4 border border-gray-200 sahi-radius cursor-pointer hover:border-sahi-green hover:bg-gray-50 transition-colors  !bg-bg-grey-light"
                          >
                            <h3 className="font-medium text-gray-900">{agency.CompanyName}</h3>
                            <p className="text-sm text-muted-foreground mt-1">{agency.Designation}</p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </>
              )}
            </>}
        </Card>

      </div>
      <SahiPrinciplesModal
        open={showPrinciplesModal}
        onOpenChange={setShowPrinciplesModal}
        onAccept={() => {
          setAcceptedTerms(true);
          setShowPrinciplesModal(false);
        }}
      />
    </div>
  )
};

export default LoginPopup;